/**
 * DispositivosPruebaPage — Usuarios/dispositivos de prueba (§8.3.5).
 * Ruta: /notificaciones/pruebas.
 *
 * Tabla de usuarios de prueba, agregar por cédula, quitar con confirmación y
 * envío de una notificación de prueba genérica para validar el pipeline.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  FlaskConical,
  Loader2,
  Plus,
  Smartphone,
  Trash2,
  XCircle,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import { enviarPruebaFn, gestionarDispositivosPruebaFn, mensajeError } from './notificacionesApi';
import {
  fmtRelativo,
  type CandidatoPrueba,
  type ResultadoPruebaDispositivo,
  type UsuarioPrueba,
} from './tiposCampana';
import styles from './DispositivosPruebaPage.module.css';

const CONTENIDO_PRUEBA_GENERICA = {
  titulo: 'Prueba de notificaciones MinSalud',
  descripcion: 'Esta es una notificación de prueba del portal.',
};

export default function DispositivosPruebaPage() {
  const { addToast } = useToast();

  const [usuarios, setUsuarios] = useState<UsuarioPrueba[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [cedula, setCedula] = useState('');
  const [errorCedula, setErrorCedula] = useState<string | null>(null);
  const [agregando, setAgregando] = useState(false);
  const [candidatos, setCandidatos] = useState<CandidatoPrueba[] | null>(null);
  const [quitarTarget, setQuitarTarget] = useState<UsuarioPrueba | null>(null);
  const [quitando, setQuitando] = useState(false);
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const [resultados, setResultados] = useState<ResultadoPruebaDispositivo[] | null>(null);

  const cargar = useCallback(async () => {
    try {
      const res = await gestionarDispositivosPruebaFn({ action: 'listar' });
      setUsuarios(res.data.testUsuarios ?? []);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudieron cargar los usuarios de prueba.'));
      setUsuarios([]);
    } finally {
      setCargando(false);
    }
  }, [addToast]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const agregar = async () => {
    const valor = cedula.trim();
    if (!valor) {
      setErrorCedula('Ingrese la cédula del usuario.');
      return;
    }
    setErrorCedula(null);
    setAgregando(true);
    try {
      const res = await gestionarDispositivosPruebaFn({ action: 'agregar', cedula: valor });
      if (res.data.requiereSeleccion) {
        // Varias cuentas de la app comparten esa cédula: el gestor elige cuál
        setCandidatos(res.data.candidatos ?? []);
        return;
      }
      setUsuarios(res.data.testUsuarios ?? []);
      setCedula('');
      addToast('success', 'Usuario de prueba agregado.');
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo agregar el usuario de prueba.'));
    } finally {
      setAgregando(false);
    }
  };

  const agregarPorUid = async (candidato: CandidatoPrueba) => {
    setAgregando(true);
    try {
      const res = await gestionarDispositivosPruebaFn({ action: 'agregar', uid: candidato.uid });
      setUsuarios(res.data.testUsuarios ?? []);
      setCandidatos(null);
      setCedula('');
      addToast('success', `${candidato.email} agregado como usuario de prueba.`);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo agregar el usuario de prueba.'));
    } finally {
      setAgregando(false);
    }
  };

  const quitar = async (u: UsuarioPrueba) => {
    setQuitando(true);
    try {
      const res = await gestionarDispositivosPruebaFn({ action: 'quitar', uid: u.uid });
      setUsuarios(res.data.testUsuarios ?? []);
      addToast('success', `${u.nombre} ya no es usuario de prueba.`);
      setQuitarTarget(null);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo quitar el usuario de prueba.'));
    } finally {
      setQuitando(false);
    }
  };

  const enviarPruebaGenerica = async () => {
    setEnviandoPrueba(true);
    setResultados(null);
    try {
      const res = await enviarPruebaFn({ contenido: CONTENIDO_PRUEBA_GENERICA });
      setResultados(res.data.resultados);
      const ok = res.data.resultados.filter((r) => r.ok).length;
      addToast(
        'success',
        `Prueba enviada: ${ok} de ${res.data.resultados.length} dispositivos aceptados.`,
      );
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo enviar la notificación de prueba.'));
    } finally {
      setEnviandoPrueba(false);
    }
  };

  const lista = usuarios ?? [];
  const totalDispositivos = lista.reduce((acc, u) => acc + u.dispositivos.length, 0);

  return (
    <MainLayout
      pageTitle="Dispositivos de prueba"
      pageSubtitle="Usuarios reales que reciben los envíos de prueba antes de publicar una campaña"
    >
      <div className={styles.page}>
        {/* ─── Agregar + acción de prueba ─── */}
        <div className={styles.toolbar}>
          <div className={styles.addForm}>
            <div className={styles.addInput}>
              <Input
                label="Agregar usuario de prueba por cédula"
                placeholder="Ej. 8-123-4567"
                value={cedula}
                onChange={(e) => setCedula(e.target.value)}
                error={errorCedula ?? undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void agregar();
                }}
              />
            </div>
            <Button variant="primary" loading={agregando} onClick={() => void agregar()}>
              <Plus size={15} aria-hidden="true" /> Agregar
            </Button>
          </div>
          <Button
            variant="secondary"
            loading={enviandoPrueba}
            disabled={totalDispositivos === 0}
            onClick={() => void enviarPruebaGenerica()}
          >
            <FlaskConical size={15} aria-hidden="true" /> Enviar notificación de prueba
          </Button>
        </div>

        {/* ─── Resultados de la prueba ─── */}
        {resultados && (
          <div className={styles.resultados} aria-live="polite">
            <h3 className={styles.resultadosTitulo}>Resultado del último envío de prueba</h3>
            <ul className={styles.resultadosLista}>
              {resultados.map((r, i) => (
                <li
                  key={`${r.plataforma}-${i}`}
                  className={r.ok ? styles.resultadoOk : styles.resultadoError}
                >
                  {r.ok ? (
                    <CheckCircle2 size={14} aria-hidden="true" />
                  ) : (
                    <XCircle size={14} aria-hidden="true" />
                  )}
                  <span>
                    {r.plataforma === 'ios' ? 'iOS' : 'Android'} —{' '}
                    {r.ok ? 'aceptada por FCM' : r.error ?? 'error'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ─── Tabla ─── */}
        <div className={styles.tableCard}>
          {cargando ? (
            <p className={styles.loading}>
              <Loader2 size={16} className={styles.spin} aria-hidden="true" /> Cargando usuarios de
              prueba…
            </p>
          ) : lista.length === 0 ? (
            <EmptyState
              variant="default"
              title="Sin usuarios de prueba"
              message="Agregue por cédula a los usuarios (con la app instalada) que recibirán las notificaciones de prueba."
            />
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Cédula</th>
                    <th>Dispositivos</th>
                    <th className={styles.actionsCol}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((u) => (
                    <tr key={u.uid}>
                      <td>
                        <span className={styles.nombre}>{u.nombre}</span>
                      </td>
                      <td>
                        <span className={styles.cedula}>{u.cedula}</span>
                      </td>
                      <td>
                        {u.dispositivos.length === 0 ? (
                          <span className={styles.sinDispositivos}>
                            Sin dispositivos registrados
                          </span>
                        ) : (
                          <div className={styles.dispositivos}>
                            {u.dispositivos.map((d, i) => (
                              <span key={`${d.plataforma}-${i}`} className={styles.dispositivo}>
                                <Badge
                                  variant={d.plataforma === 'ios' ? 'info' : 'success'}
                                  size="sm"
                                >
                                  <span className={styles.badgeInner}>
                                    <Smartphone size={11} aria-hidden="true" />
                                    {d.plataforma === 'ios' ? 'iOS' : 'Android'}
                                  </span>
                                </Badge>
                                <span className={styles.actividad}>
                                  {d.actualizadoEn ? fmtRelativo(d.actualizadoEn) : 'sin actividad'}
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={styles.actionBtnDanger}
                            title={`Quitar a ${u.nombre} de las pruebas`}
                            aria-label={`Quitar a ${u.nombre} de las pruebas`}
                            onClick={() => setQuitarTarget(u)}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                            <span>Quitar</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── Elegir cuenta cuando la cédula tiene varias ─── */}
      <Modal
        open={candidatos !== null}
        onClose={() => setCandidatos(null)}
        title="Varias cuentas con esa cédula"
      >
        <div className={styles.confirmBody}>
          <p className={styles.confirmTexto}>
            La cédula <strong>{cedula.trim()}</strong> está registrada en varias cuentas de la
            app. Seleccione la cuenta (por correo) que usará para las pruebas:
          </p>
          <ul className={styles.candidatosLista}>
            {(candidatos ?? []).map((c) => (
              <li key={c.uid}>
                <button
                  type="button"
                  className={styles.candidatoBtn}
                  disabled={agregando || c.yaAgregado}
                  onClick={() => void agregarPorUid(c)}
                >
                  <span className={styles.candidatoEmail}>{c.email || c.uid}</span>
                  <span className={styles.candidatoMeta}>
                    <Badge variant={c.dispositivos > 0 ? 'success' : 'neutral'} size="sm">
                      {c.dispositivos === 1
                        ? '1 dispositivo'
                        : `${c.dispositivos} dispositivos`}
                    </Badge>
                    {c.yaAgregado && (
                      <Badge variant="info" size="sm">Ya agregado</Badge>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className={styles.confirmAcciones}>
            <Button variant="secondary" onClick={() => setCandidatos(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── Confirmar quitar ─── */}
      <Modal
        open={quitarTarget !== null}
        onClose={() => setQuitarTarget(null)}
        title="Quitar usuario de prueba"
      >
        {quitarTarget && (
          <div className={styles.confirmBody}>
            <p className={styles.confirmTexto}>
              ¿Desea quitar a <strong>{quitarTarget.nombre}</strong> ({quitarTarget.cedula}) de
              los usuarios de prueba? Dejará de recibir los envíos de prueba del portal.
            </p>
            <div className={styles.confirmAcciones}>
              <Button variant="secondary" onClick={() => setQuitarTarget(null)}>
                Cancelar
              </Button>
              <Button variant="danger" loading={quitando} onClick={() => void quitar(quitarTarget)}>
                Sí, quitar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
}
